import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { isAxiosError } from "axios";
import { format } from "date-fns";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { DATE_FORMAT, USER_STATUS } from "../../../assets/data/common";
import {
  blockUser,
  terminateUser,
  unblockUser,
} from "../../../axios/User/UserApi";
import CustomDateTimeRangePicker from "../../../components/CustomDateTimeRangePicker";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import { CustomDialog } from "../../../components/StyledComponents";
import { useLoading } from "../../../context/LoadingContext";

const StatusUserDialog = ({ info, mode, onRefresh, onClose }) => {
  const [show, hide] = useLoading();
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isTerminal = mode === USER_STATUS.terminate;

  const { action, title, content, contentExtra, messageSuccess } = useMemo(() => {
    switch (mode) {
      case USER_STATUS.unblock:
        return {
          title: "common.content-106",
          content: "common.content-107",
          messageSuccess: "common.content-105",
          action: unblockUser,
        };
      case USER_STATUS.block:
        return {
          title: "common.block-user",
          content: "common.are-you-sure-you-want-to-block-this-user",
          messageSuccess: "common.content-104",
          action: blockUser,
        };

      default:
        return {
          title: "common.content-151",
          content: "common.content-152",
          contentExtra: "common.content-156",
          messageSuccess: "common.content-153",
          action: terminateUser,
        };
    }
  }, [mode]);

  const handleCloseDialog = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setIsSubmitting(true);
      show();
      const dateEnd = selectedDate
        ? format(selectedDate, DATE_FORMAT.yyyyMMdd)
        : null;
      await action(info.userId, dateEnd, localStorage.getItem("i18nextLngId"));
      toast.success(t(messageSuccess));
      onRefresh();
      onClose();
    } catch (error) {
      if (error.response) {
        if (error.response.status !== 401) {
          toast.error(t(error?.response.data));
        }
      } else {
        toast.error(t('common.network-timeout'));
      }
    } finally {
      setIsSubmitting(false);
      hide();
    }
  };

  return (
    <CustomDialog
      transitionDuration={300}
      maxWidth={"xl"}
      open
      onClose={handleCloseDialog}
      loading={isSubmitting}
      keepMounted
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold ">
              {t(title)}
            </p>
          </div>
          <GrayIconButton onClick={handleCloseDialog} disabled={isSubmitting}>
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>

      <DialogContent className="!py-0">
        <p className="py-4">
          {t(content)}
          {contentExtra ? ` ${t(contentExtra)}` : null}
        </p>
        {isTerminal && (
          <div className="flex items-center gap-2 pb-4">
            <p className="shrink-0">{t("common.content-157")}</p>
            <div className="flex-1 min-w-0">
              <CustomDateTimeRangePicker
                mode="single"
                value={selectedDate}
                onChange={setSelectedDate}
                disableTime
                showOnlyDate
              />
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <div className="flex gap-2 px-4 pt-2 pb-4">
          <ControlpanelSecondaryButton onClick={handleCloseDialog} disabled={isSubmitting}>
            <p>{t("button.cancel")}</p>
          </ControlpanelSecondaryButton>
          <ControlpanelPrimaryButton onClick={handleSubmit} disabled={isSubmitting}>
            <p>{t("button.ok")}</p>
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default StatusUserDialog;
